import { useAppSelector } from '@/app/hooks'
import { RootState } from '@/app/store'
import { ModalWrapper } from '@/components/atoms/modal-wrapper'
import { CUSTOM_BORDER } from '@/components/template/custom-design-style'
import { css } from '@emotion/react'
import React, { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

type UploadedImage = {
  previewUrl: string
}

type Props = {
  label?: string
  image?: UploadedImage
  sampleImageUrl?: string
  loading?: boolean
  boxHeightPx?: number
  onSelectFile: (file: File) => void
  onRequestCamera: () => void
}

export const DocumentUploadSlot: React.FC<Props> = ({
  label,
  image,
  sampleImageUrl,
  boxHeightPx = 204,
  loading = false,
  onSelectFile,
  onRequestCamera,
}) => {
  const { t } = useTranslation()
  const design = useAppSelector(state => state.design)
  const [isChooserOpen, setIsChooserOpen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const boxHeightStyle = useMemo(() => css({ height: `${boxHeightPx}px` }), [boxHeightPx])
  const boxStyle = useMemo(() => getBoxStyle(design), [design])
  const loadingSpinnerStyle = useMemo(() => getLoadingSpinnerStyle(design), [design])
  const selectButtonStyle = useMemo(() => getSelectButtonStyle(design), [design])
  const chooserRowStyle = useMemo(() => getChooserRowStyle(design), [design])

  const openChooser = () => {
    if (loading) {
      return
    }
    setIsChooserOpen(true)
  }

  const handleChooseCamera = () => {
    setIsChooserOpen(false)
    onRequestCamera()
  }

  const handleChooseFile = () => {
    setIsChooserOpen(false)
    fileInputRef.current?.click()
  }

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    // 同じファイルを選び直しても onChange が発火するようにする
    event.target.value = ''
    if (file) {
      onSelectFile(file)
    }
  }

  return (
    <div>
      {label && (
        <div css={labelRowStyle}>
          <p css={labelTextStyle}>{label}</p>
          {image && (
            <svg css={checkIconStyle} viewBox="0 0 20 20" aria-hidden>
              {/* 施設が設定した背景色と文字色の組み合わせを使う。単独の色を選ぶとチェックが背景に埋もれる */}
              <circle cx="10" cy="10" r="10" fill={design.button[0].backgroundColor} />
              <path
                d="M5.5 10.5L8.5 13.5L14.5 7"
                stroke={design.button[0].textColor}
                strokeWidth="1.6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </div>
      )}

      <div css={[boxStyle, boxHeightStyle]} className={!image && !sampleImageUrl ? CUSTOM_BORDER : undefined}>
        {image ? (
          <img src={image.previewUrl} alt={label} css={previewImageStyle} />
        ) : sampleImageUrl ? (
          <img src={sampleImageUrl} alt="" css={sampleImageStyle} />
        ) : (
          <div css={placeholderStyle}>
            <svg viewBox="0 0 48 32" width="48" height="32" aria-hidden>
              <rect x="1" y="1" width="46" height="30" rx="4" fill="none" stroke={design.form.borderColor} strokeWidth="2" />
              <circle cx="14" cy="14" r="4" fill="none" stroke={design.form.borderColor} strokeWidth="2" />
              <path d="M2 27L16 15L26 24L34 17L46 27" fill="none" stroke={design.form.borderColor} strokeWidth="2" />
            </svg>
          </div>
        )}
        {loading && (
          <div css={loadingOverlayStyle}>
            <span css={loadingSpinnerStyle} />
          </div>
        )}
      </div>

      <input ref={fileInputRef} type="file" accept="image/*" css={hiddenInputStyle} onChange={handleFileInputChange} />

      <button type="button" css={selectButtonStyle} disabled={loading} onClick={openChooser}>
        {t('Take a photo or choose a file')}
      </button>

      <ModalWrapper isOpen={isChooserOpen} onClose={() => setIsChooserOpen(false)} shouldCloseOnOverlayClick modalStyle={chooserModalStyle}>
        <div css={chooserRowStyle} onClick={handleChooseCamera}>
          {t('Take a photo')}
        </div>
        <div css={chooserRowStyle} onClick={handleChooseFile}>
          {t('Choose from photos or files')}
        </div>
      </ModalWrapper>
    </div>
  )
}

const labelRowStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '0.75rem',
})

const labelTextStyle = css({
  fontSize: '14px',
  fontWeight: 'bold',
  letterSpacing: '0.7px',
})

const checkIconStyle = css({
  width: '18px',
  height: '18px',
  flexShrink: 0,
})

const getBoxStyle = (design: RootState['design']) =>
  css({
    position: 'relative',
    width: '100%',
    borderRadius: '10px',
    overflow: 'hidden',
    background: design.background.backgroundColor,
  })

const placeholderStyle = css({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

const sampleImageStyle = css({
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  display: 'block',
})

const previewImageStyle = css({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
})

const loadingOverlayStyle = css({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(255,255,255,0.6)',
})

const getLoadingSpinnerStyle = (design: RootState['design']) =>
  css({
    width: '24px',
    height: '24px',
    border: `3px solid ${design.form.borderColor}`,
    borderTopColor: design.icon.color,
    borderRadius: '50%',
    animation: 'document-upload-slot-spin 0.8s linear infinite',
    '@keyframes document-upload-slot-spin': {
      to: { transform: 'rotate(360deg)' },
    },
  })

const hiddenInputStyle = css({
  display: 'none',
})

const getSelectButtonStyle = (design: RootState['design']) =>
  css({
    width: '100%',
    height: '48px',
    marginTop: '1rem',
    borderRadius: '6px',
    border: `1px solid ${design.button[1].borderColor}`,
    background: 'none',
    color: design.button[1].textColor,
    fontSize: '16px',
    cursor: 'pointer',
    '&:disabled': {
      opacity: 0.3,
    },
  })

const chooserModalStyle = {
  inset: 'auto 1rem 1rem 1rem',
  top: 'auto',
  borderRadius: '10px',
  padding: 0,
}

const getChooserRowStyle = (design: RootState['design']) =>
  css({
    padding: '1.1rem 1.5rem',
    fontSize: '16px',
    textAlign: 'center',
    cursor: 'pointer',
    '&:not(:last-of-type)': {
      borderBottom: `1px solid ${design.form.borderColor}`,
    },
  })
